import React, { useState, useRef, useCallback } from "react";
import Button from "@mui/material/Button";
import PrintIcon from "@mui/icons-material/Print";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useListContext, useUnselectAll } from "react-admin";
import ls from "local-storage";
import { useCustomAllRecords } from "../Common/useCustomAllRecords";
import { useIsBusinessPlan } from "../../helpers/subscriptionPlan";
import {
  CMD_END_PAGE_PRINT,
  CMD_SET_LABEL_DENSITY,
  CMD_SET_LABEL_TYPE,
  CMD_START_PAGE_PRINT,
  CMD_START_PRINT,
  fillTextMultiLine,
  makeNiimbotSendImagePackets,
  niimbotMmToPx,
  niimbotSetLabelDimensions,
  niimbotToPacket,
  useNiimbotPrinter,
  usePrintJobQueue,
  useSerialPort,
} from "../SerialPrint/serial";
import { canvasGetImageData } from "../SerialPrint/helper";

const BulkPrintLabel = (props) => {
  const { isOpen, setIsOpen } = props;
  const { selectedIds, resource } = useListContext();
  const allRecords = useCustomAllRecords();
  const isBusinessPlan = useIsBusinessPlan();
  const canvasRef = useRef(null);
  const hiddenCanvasRef = useRef(null);

  const [canvasHeightMm, setCanvasHeightMm] = useState(() => {
    const stored = ls.get("canvasHeightMm");
    return stored !== null ? stored : 30;
  });
  const [canvasWidthMm, setCanvasWidthMm] = useState(() => {
    const stored = ls.get("canvasWidthMm");
    return stored !== null ? stored : 50;
  });
  const [canvasTextSize, setCanvasTextSize] = useState(() => {
    const stored = ls.get("canvasTextSize");
    return stored !== null ? stored : 30;
  });
  const { data: serialPort } = useSerialPort();
  useNiimbotPrinter();
  const { data: printJobQueue = [], mutate: mutatePrintJobQueue } =
    usePrintJobQueue();
  const unselectAll = useUnselectAll(resource);

  const clearCanvas = useCallback((canvasRef) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }, []);

  const handleDialogClose = React.useCallback(() => {
    setIsOpen(false);
    unselectAll();
  }, [setIsOpen, unselectAll]);

  const originals = allRecords.filter((rec) => selectedIds.includes(rec._id));

  React.useEffect(() => {
    if (canvasRef.current && originals.length > 0) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.textAlign = "center";
      ctx.font = canvasTextSize + "px Arial";
      ctx.fillStyle = "black";
      const item =
        originals[
          printJobQueue.length > 0 ? originals.length - printJobQueue.length : 0
        ];
      const customerName = item.order.customerName || "";
      const itemDescription = item.description;
      const keyword = item.keyword ? item.keyword.key : "";
      clearCanvas(canvasRef);
      fillTextMultiLine(
        ctx,
        `${customerName}\n${itemDescription}\n${keyword}\n$${item.price}`,
        canvas.width / 2,
        canvas.height / 5
      );
    }
  }, [clearCanvas, originals, printJobQueue, canvasRef, canvasTextSize]);

  const handleConfirm = React.useCallback(async () => {
    const canvas = hiddenCanvasRef.current;
    const ctx = canvas.getContext("2d");
    // Set canvas properties
    ctx.textAlign = "center";
    ctx.font = canvasTextSize + "px Arial";
    ctx.fillStyle = "black";

    const allJobsFullPrintPackets = originals.map((item, idx) => {
      const customerName = item.order.customerName || "";
      const itemDescription = item.description;
      const keyword = item.keyword ? item.keyword.key : "";

      clearCanvas(hiddenCanvasRef);

      fillTextMultiLine(
        ctx,
        `${customerName}\n${itemDescription}\n${keyword}\n$${item.price}`,
        canvas.width / 2,
        canvas.height / 5
      );

      const [imageData, width, height] = canvasGetImageData(
        canvas,
        0,
        0,
        canvas.width,
        canvas.height,
        false //canvasFlipXY
      );
      const setupPackets = [
        niimbotToPacket(CMD_SET_LABEL_TYPE, [1]),
        niimbotToPacket(CMD_SET_LABEL_DENSITY, [3]),
        niimbotToPacket(CMD_START_PRINT, [1]),
        niimbotToPacket(CMD_START_PAGE_PRINT, [1]),
        niimbotSetLabelDimensions(width, height),
      ].map((packet) => ({
        data: packet,
        waitReceive: true,
        isPrinting: false,
      }));

      const imageDataPackets = makeNiimbotSendImagePackets(
        width,
        height,
        imageData
      ).map((packet) => ({
        data: packet,
        waitReceive: false,
        isPrinting: false,
      }));

      const waitForCompletionPackets = [
        {
          data: niimbotToPacket(CMD_END_PAGE_PRINT, [1]),
          isPrinting: true,
          waitReceive: false,
        },
      ];

      const fullPrintPackets = setupPackets.concat(
        imageDataPackets,
        waitForCompletionPackets
      );
      return fullPrintPackets;
    });

    mutatePrintJobQueue(allJobsFullPrintPackets, {
      revalidate: false,
    });
  }, [clearCanvas, mutatePrintJobQueue, originals, canvasTextSize]);

  return (
    <>
      {/* <Loader open={processing}>
        {`DO NOT CLOSE THIS WINDOW !\n Processing...`}
      </Loader> */}
      <Dialog open={isOpen} aria-labelledby="responsive-dialog-title">
        <DialogTitle id="responsive-dialog-title">Print labels</DialogTitle>
        <DialogContent>
          <div>
            Notes:
            <ul>
              <li>Suggested printer: Niimbot B21</li>
              <li>Suggested paper size: 50mm * 30mm</li>
              <li>
                <a
                  href="https://learn.boxify.io/kb/sticker-printer/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Tutorial Link
                </a>
              </li>
            </ul>
          </div>
          <canvas
            ref={hiddenCanvasRef}
            width={niimbotMmToPx(canvasWidthMm)}
            height={niimbotMmToPx(canvasHeightMm)}
            style={{
              background: "white",
              border: "1px solid black",
              display: "none",
            }}
          />
          <canvas
            ref={canvasRef}
            width={niimbotMmToPx(canvasWidthMm)}
            height={niimbotMmToPx(canvasHeightMm)}
            style={{
              background: "white",
              border: "1px solid black",
            }}
          />
          <div>
            <label>Label Size:</label>
            <select
              value={`${canvasWidthMm}x${canvasHeightMm}`}
              onChange={(e) => {
                const [width, height] = e.target.value.split("x");
                setCanvasWidthMm(width);
                setCanvasHeightMm(height);
                ls.set("canvasWidthMm", width);
                ls.set("canvasHeightMm", height);
              }}
              style={{ marginLeft: "10px" }}
            >
              <option value="50x30">50x30 mm</option>
              <option value="40x20">40x20 mm</option>
              <option value="30x20">30x20 mm</option>
            </select>
          </div>
          <div>
            <label>Text Size (px):</label>
            <input
              type="number"
              value={canvasTextSize}
              onChange={(e) => {
                setCanvasTextSize(e.target.value);
                ls.set("canvasTextSize", e.target.value);
              }}
              style={{ marginLeft: "10px" }}
            />
          </div>
          {!isBusinessPlan && (
            <h4 style={{ color: "red" }}>This is a business plan feature.</h4>
          )}
          <ul>
            {originals.map((i, idx) => {
              return (
                <li key={i._id}>{`${i.description} - ${
                  i.order.customerName || ""
                }`}</li>
              );
            })}
          </ul>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={handleConfirm}
            color="primary"
            autoFocus
            disabled={!!serialPort || !isBusinessPlan} // disable serial port when user has already printed once. Force user to reopen window for an other print
          >
            Print
          </Button>
          <Button
            variant="contained"
            onClick={handleDialogClose}
            color="secondary"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const BulkPrintLabelButton = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSerialApiSupported] = useState("serial" in navigator);
  return (
    <>
      <Button
        onClick={() => {
          setIsOpen(true);
        }}
        disabled={!isSerialApiSupported}
      >
        <PrintIcon />
        Label
      </Button>
      {isOpen && (
        <BulkPrintLabel {...props} isOpen={isOpen} setIsOpen={setIsOpen} />
      )}
    </>
  );
};

export default BulkPrintLabelButton;
